// core/action/accountStatementActions.js
import { accountStatementAPI } from './api';
import { toast } from 'react-toastify';

export const accountStatementActions = {
    // Get account statement
    getStatement: async (params = {}) => {
        try {
            const response = await accountStatementAPI.get(params);
            
            if (response.data) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: null
                };
            }
        } catch (error) {
            console.error('Error fetching account statement:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch account statement',
                data: null
            };
        }
    },

    // Export statement as PDF
    exportToPDF: async (params = {}) => {
        try {
            const response = await accountStatementAPI.exportPDF(params);
            return response;
        } catch (error) {
            console.error('Error exporting statement:', error);
            toast.error('Failed to export statement');
            return null;
        }
    },

    // Export statement as Excel
    exportToExcel: async (params = {}) => {
        try {
            const response = await accountStatementAPI.exportExcel(params);
            return response;
        } catch (error) {
            console.error('Error exporting statement:', error);
            toast.error('Failed to export statement');
            return null;
        }
    },

    // Get all accounts for dropdown
    getAccounts: async () => {
        try {
            const response = await accountStatementAPI.getAccounts();
            
            if (response.data && response.data.accounts) {
                return {
                    success: true,
                    data: response.data.accounts
                };
            } else if (Array.isArray(response.data)) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch accounts',
                data: []
            };
        }
    },

    // Format currency helper
    formatCurrency: (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    },

    // Format date helper
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    },

    // Get transaction type badge class
    getTransactionTypeBadge: (type) => {
        const classes = {
            'credit': 'bg-success',
            'debit': 'bg-danger',
            'transfer': 'bg-info'
        };
        return classes[type?.toLowerCase()] || 'bg-secondary';
    },

    // Get transaction status badge class
    getStatusBadge: (status) => {
        const classes = {
            'cleared': 'bg-success',
            'pending': 'bg-warning',
            'cancelled': 'bg-danger',
            'bounced': 'bg-danger'
        };
        return classes[status?.toLowerCase()] || 'bg-secondary';
    },

    // Format transaction type for display
    formatTransactionType: (type) => {
        const types = {
            'credit': 'Credit',
            'debit': 'Debit',
            'transfer': 'Transfer'
        };
        return types[type?.toLowerCase()] || type;
    },

    // Get account type icon class
    getAccountTypeIcon: (type) => {
        const icons = {
            'savings': '🏦',
            'current': '💼',
            'cash': '💵',
            'credit_card': '💳',
            'loan': '💰'
        };
        return icons[type?.toLowerCase()] || '🏦';
    }
};

export default accountStatementActions;